'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from 'react';
import * as RDialog from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';
import { isFromGlobalOverlay } from '../../lib/global-overlay';
import { getModalHost, registerModalStackEntry } from '../../lib/modal-stack';
import { InsideModalProvider, PortalContainerProvider } from '../../lib/portal-container';
import { SGhostButton } from '../SGhostButton';

/**
 * @internal
 * 내부 전용 타입. 소비자에게 노출되지 않으며(패키지 barrel export 제외),
 * SConfirmModal/SActionModal/SLoadingModal 내부에서만 사용한다.
 */
export interface SModalContainerProps {
  /** 표시 여부 (제어) */
  open?: boolean;
  /** 표시 상태 변경 */
  onOpenChange?: (open: boolean) => void;
  /** 접근성 제목 (스크린리더용). 시각 제목은 children이 담당 */
  ariaTitle?: string;
  /** 우측 상단 닫기 버튼 */
  showClose?: boolean;
  /** 닫기(X) 버튼 클릭 시 발생 (sd-modal-container 닫기 버튼 대응) */
  onClose?: () => void;
  /**
   * 백드롭 클릭·ESC로 닫히지 않고 흔들림(shake) 애니메이션 (sd-modal-container persistent).
   * **기본값 true** — 모달은 작성 중인 내용을 잃지 않도록 임의 닫힘을 막는 것이 기본이다.
   * 닫기 경로는 X 버튼과 모달 안의 버튼뿐이다. false로 주면 백드롭·ESC 닫기가 열린다.
   */
  persistent?: boolean;
  /** 너비/높이 */
  width?: number | string;
  height?: number | string;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const len = (v?: number | string) =>
  v === undefined ? undefined : typeof v === 'number' ? `${v}px` : v;

// overlay-out(0.3s)이 끝난 뒤 Portal을 제거한다.
const CLOSE_ANIMATION_DURATION = 300;

/**
 * SModalContainer — sd-modal-container 포팅 (Radix Dialog 기반).
 * 모든 모달(Confirm/Action/Loading)의 공통 컨테이너. 딤 오버레이 + 중앙 흰 카드.
 * persistent(기본 true)면 백드롭/ESC 닫기를 막고 흔들림 애니메이션을 준다.
 *
 * @internal
 * 내부 전용 컴포넌트다. 패키지 public export(src/index.ts)에서 의도적으로 제외돼 있어
 * 소비자는 직접 import 할 수 없고, SConfirmModal/SActionModal/SLoadingModal 세 모달의
 * 공통 컨테이너로만 조합해 쓴다. barrel(src/index.ts)에 다시 추가하지 말 것.
 */
export function SModalContainer({
  open,
  onOpenChange,
  ariaTitle = '모달',
  showClose = false,
  onClose,
  persistent = true,
  width,
  height,
  children,
  className,
  style,
}: SModalContainerProps) {
  const [shaking, setShaking] = useState(false);
  // Radix Portal이 open=false 즉시 자식을 제거하지 않도록 닫힘 중에도 유지한다.
  const [rendered, setRendered] = useState(() => Boolean(open));
  const contentRef = useRef<HTMLDivElement>(null);
  const stackRegistrationRef = useRef<ReturnType<typeof registerModalStackEntry> | null>(null);
  // ModalHost 안에서 열린 순서에 따라 local layer를 배정한다.
  // 닫힘 애니메이션 동안에는 기존 layerIndex를 유지하고, 새로 열릴 때만 새 레이어를 받는다.
  const [layerIndex, setLayerIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }

    if (!rendered) return;
    const timer = window.setTimeout(() => setRendered(false), CLOSE_ANIMATION_DURATION);
    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;

    const registration = registerModalStackEntry();
    stackRegistrationRef.current = registration;
    setLayerIndex(registration.layerIndex);
    return () => {
      registration.unregister();
      if (stackRegistrationRef.current === registration) {
        stackRegistrationRef.current = null;
      }
    };
  }, [open]);

  const modalHost = getModalHost();
  // 모달 안에서 열린 floating(SSelect/SPopover/SDatePicker 등)이 같은 모달의 backdrop 위로
  // 뜨도록 Content 자손에 전용 portal target을 둔다. 전역 z-index는 ModalHost 하나만 가진다.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // 클래스 재적용만으로는 애니메이션이 재시작되지 않으므로 off→on으로 강제 재시작한다.
  const triggerShake = useCallback(() => {
    setShaking(false);
    requestAnimationFrame(() => setShaking(true));
  }, []);

  const guardClose = persistent
    ? (e: Event) => {
        e.preventDefault();
        // 여러 Radix Dialog가 같은 ESC/outside 이벤트를 받을 수 있으므로
        // 이벤트는 모두 막되 shake는 현재 최상단 모달만 실행한다.
        if (stackRegistrationRef.current?.isTop()) triggerShake();
      }
    : undefined;

  // 토스트·로딩은 모달보다 위에 뜨지만 DOM 상으로는 body 직속이라 Radix 는 '모달 바깥'으로 본다.
  // 그대로 두면 토스트 닫기 버튼을 누른 것만으로 모달이 닫히거나(일반) 흔들린다(persistent).
  const guardInteractOutside = (e: Event) => {
    if (isFromGlobalOverlay(e)) {
      e.preventDefault();
      return;
    }
    guardClose?.(e);
  };

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal container={modalHost} forceMount={rendered ? true : undefined}>
        <div className="pointer-events-none absolute inset-0" style={{ zIndex: layerIndex ?? 0 }}>
          <RDialog.Overlay
            forceMount={rendered ? true : undefined}
            className="pointer-events-auto absolute inset-0 bg-black/40 data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out"
            style={{ zIndex: 0, animationFillMode: 'forwards' }}
          />
          {/*
            Overlay와 Content를 하나의 모달 레이어 안에 둔다. 모달 간 전역 z-index를 올리지
            않고 Host 내부의 layerIndex만 비교하므로 Toast/Loading과 충돌하지 않는다.
            중앙정렬은 이 래퍼가 담당하고, Content는 scale 애니메이션만 수행한다.
          */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 1 }}
          >
            <RDialog.Content
              forceMount={rendered ? true : undefined}
              ref={contentRef}
              className="pointer-events-auto outline-none data-[state=open]:animate-modal-in data-[state=closed]:animate-modal-out"
              style={{ animationFillMode: 'forwards' }}
              // Radix 기본 동작은 열릴 때 첫 tabbable(= 닫기 X 버튼)로 포커스를 옮긴다. 직전 조작이
              // 키보드였던 환경에서는 이 프로그래매틱 포커스에도 :focus-visible 이 매칭돼 X 에 링이 그려진다.
              // 대신 Content(자체 tabIndex=-1, outline-none)로 포커스를 보내 포커스 트랩·ESC·스크린리더
              // 안내는 그대로 두고 링만 없앤다.
              onOpenAutoFocus={e => {
                e.preventDefault();
                contentRef.current?.focus({ preventScroll: true });
              }}
              onEscapeKeyDown={guardClose}
              onPointerDownOutside={guardInteractOutside}
              onInteractOutside={guardInteractOutside}
            >
              <RDialog.Title className="sr-only">{ariaTitle}</RDialog.Title>
              {/*
                카드 높이/너비는 뷰포트(dvh/dvw)에서 상하좌우 여백 24px 씩을 뺀 값으로 클램핑한다.
                height/width prop 이나 콘텐츠가 화면보다 커도 카드가 화면 밖으로 넘쳐 잘리지 않는다
                (max-height/max-width 가 height/width 보다 우선). flex 컬럼으로 두어, 세로가 클램핑됐을 때
                각 모달의 본문 영역(min-h-0 + overflow-auto)이 줄어들며 스크롤을 갖게 한다. 가로는 텍스트가
                래핑되며 줄어드는 것으로 충분해 별도 overflow-x 처리는 두지 않는다.
                카드 자체는 radius 유지와 닫기(X) 고정을 위해 overflow-hidden 을 유지.
                consumer 가 className 으로 max-h-*, max-w-* 를 주면 tailwind-merge 로 덮어쓸 수 있다.
              */}
              <div
                className={cn(
                  'relative flex max-h-[calc(100dvh-48px)] max-w-[calc(100dvw-48px)] flex-col overflow-hidden rounded-[var(--cmp-modal-radius)] bg-[var(--cmp-modal-bg)] shadow-normal-md',
                  shaking && 'animate-modal-shake',
                  className,
                )}
                style={{ width: len(width), height: len(height), ...style }}
                onAnimationEnd={e => {
                  if (e.animationName === 'modal-shake') setShaking(false);
                }}
              >
                {showClose && (
                  // 원본 모달의 닫기 버튼은 sd-ghost-button(icon="close") — SGhostButton 재사용
                  <SGhostButton
                    icon="close"
                    ariaLabel="닫기"
                    onClick={() => {
                      onClose?.();
                      onOpenChange?.(false);
                    }}
                    className="absolute right-[16px] top-[16px] z-10"
                  />
                )}
                <InsideModalProvider value={true}>
                  <PortalContainerProvider value={portalContainer}>
                    {children}
                  </PortalContainerProvider>
                </InsideModalProvider>
              </div>
              {/*
                반드시 Content의 자손이어야 한다. Radix Dialog의 hideOthers/focus trap 범위를
                유지하면서 모달 내부 floating을 같은 layer의 Content 위로 표시한다.
              */}
              <div
                ref={setPortalContainer}
                className="pointer-events-auto fixed left-0 top-0"
                style={{ zIndex: 2 }}
              />
            </RDialog.Content>
          </div>
        </div>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
